import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Wrench, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, formatErr } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-1 bg-[#0F4C81] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1523552322653-4ca2658acbb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxwbHVtYmluZyUyMGluZHVzdHJpYWwlMjBwaXBlcyUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5NjQ5ODYyfDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center">
              <Wrench className="text-[#0F4C81]" size={22} strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-bold text-xl">PlumbOps</div>
              <div className="text-xs text-blue-200">FIELD OPS · CRM · ONTARIO</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
              Run your plumbing business like a Fortune 500.
            </h1>
            <p className="mt-6 text-blue-100 text-base leading-relaxed">
              Customers, estimates, invoices and payments — all in one place. Built for Ontario contractors, with HST done right.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
              <div className="border-l border-blue-300/40 pl-3"><div className="font-display text-2xl font-bold tabular">13%</div><div className="text-blue-200 text-xs mt-1">HST handled</div></div>
              <div className="border-l border-blue-300/40 pl-3"><div className="font-display text-2xl font-bold tabular">$</div><div className="text-blue-200 text-xs mt-1">CAD billing</div></div>
              <div className="border-l border-blue-300/40 pl-3"><div className="font-display text-2xl font-bold tabular">PDF</div><div className="text-blue-200 text-xs mt-1">Invoices</div></div>
            </div>
          </div>
          <div className="text-xs text-blue-200">© 2026 PlumbOps</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#0F4C81] flex items-center justify-center">
              <Wrench className="text-white" size={22} strokeWidth={2} />
            </div>
            <div className="font-display font-bold text-xl">PlumbOps</div>
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to your account to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/10"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 uppercase tracking-wider">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/10"
                required
              />
            </div>

            {err && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">{err}</div>}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in..." : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
